import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface TaskData {
  id: string;
  title?: string;
  name?: string;
  description?: string;
  status: string;
  task_status?: string;
  priority?: string;
  difficulty?: string;
  source_type?: string;
  created_at?: string;
  updated_at?: string;
  comments?: string;
  remarks?: string;
  assigned_to?: string;
  owner?: string; // For growth tasks backward compatibility
  submitted_by?: string;
  brand_id?: string;
  sprint_id?: string;
  due_date?: string;
  images?: any[];
  attachments?: any[];
  slug?: string;
  user_id?: string;
  watchers?: string[];
  tag_ids?: string[];
  timeSpent?: number;
  time_spent?: number;
  brands?: {
    name: string;
    slug: string;
  };
  sprint?: {
    id: string;
    name: string;
    start_date?: string;
    end_date?: string;
  };
  growth_sprints?: {
    id: string;
    name: string;
    start_date?: string;
    end_date?: string;
  };
  type?: 'foundational' | 'growth' | 'recurring';
}

interface TaskPopupContextType {
  isOpen: boolean;
  selectedTask: TaskData | null;
  taskType: 'foundational' | 'growth' | 'recurring';
  openTaskPopup: (task: TaskData, taskType: 'foundational' | 'growth' | 'recurring') => void;
  closeTaskPopup: () => void;
  updateSelectedTask: (updatedTask: Partial<TaskData>) => void;
  onStatusUpdate?: (taskId: string, status: string) => void;
  onCommentsUpdate?: (taskId: string, comments: string) => void;
  onRefetch?: () => void;
  setCallbacks: (callbacks: {
    onStatusUpdate?: (taskId: string, status: string) => void;
    onCommentsUpdate?: (taskId: string, comments: string) => void;
    onRefetch?: () => void;
  }) => void;
}

const TaskPopupContext = createContext<TaskPopupContextType | undefined>(undefined);

interface TaskPopupProviderProps {
  children: ReactNode;
}

export const TaskPopupProvider = ({ children }: TaskPopupProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<TaskData | null>(null);
  const [taskType, setTaskType] = useState<'foundational' | 'growth' | 'recurring'>('foundational');
  const [onStatusUpdate, setOnStatusUpdate] = useState<((taskId: string, status: string) => void) | undefined>();
  const [onCommentsUpdate, setOnCommentsUpdate] = useState<((taskId: string, comments: string) => void) | undefined>();
  const [onRefetch, setOnRefetch] = useState<(() => void) | undefined>();

  const openTaskPopup = (task: TaskData, type: 'foundational' | 'growth' | 'recurring') => {
    setSelectedTask(task);
    setTaskType(type);
    setIsOpen(true);
  };

  const closeTaskPopup = () => {
    setIsOpen(false);
    // Note: We keep the selectedTask and form data for persistence
  };

  const updateSelectedTask = (updatedTask: Partial<TaskData>) => {
    if (selectedTask) {
      setSelectedTask({ ...selectedTask, ...updatedTask });
    }
  };

  const setCallbacks = (callbacks: {
    onStatusUpdate?: (taskId: string, status: string) => void;
    onCommentsUpdate?: (taskId: string, comments: string) => void;
    onRefetch?: () => void;
  }) => {
    setOnStatusUpdate(() => callbacks.onStatusUpdate);
    setOnCommentsUpdate(() => callbacks.onCommentsUpdate);
    setOnRefetch(() => callbacks.onRefetch);
  };

  const value: TaskPopupContextType = {
    isOpen,
    selectedTask,
    taskType,
    openTaskPopup,
    closeTaskPopup,
    updateSelectedTask,
    onStatusUpdate,
    onCommentsUpdate,
    onRefetch,
    setCallbacks,
  };

  return (
    <TaskPopupContext.Provider value={value}>
      {children}
    </TaskPopupContext.Provider>
  );
};

export const useTaskPopup = () => {
  const context = useContext(TaskPopupContext);
  if (context === undefined) {
    throw new Error('useTaskPopup must be used within a TaskPopupProvider');
  }
  return context;
};
